import React, { useState } from 'react';
import { Form, FormControl, FormGroup, FormLabel } from 'react-bootstrap';

import { Regions } from '../../constants/states';
import { useFleet, useInput } from '../../hooks';
import { EventType } from '../../interfaces';
import { PostPermittedPlate, PriviledgedGroup } from '../../interfaces/whitelisted_plate';
import { invalidatePermittedPlates, useBackwardsCompatiblePlates, usePermittedPlatesGroups } from '../../queries';
import { PermittedPlatesService } from '../../services';
import { getDefaultState, humanizeEnum } from '../../utils';
import { Alert, Button, ButtonTag, ButtonsContainer, H3, Hr, Modal, Padded, ProgressButton } from '../atoms';
import { EventTypesFilterBrowser } from '../filter/EventTypesFilterBrowser';

interface Props {
  toggle: () => void;
  plate?: PostPermittedPlate | null;
}

export function AddPlateModal(props: Props) {
  const { toggle } = props;
  const [isSaving, setSaving] = useState(false);
  const fleet = useFleet();
  const { data } = useBackwardsCompatiblePlates();
  const exceptions = data ?? [];
  const groups = usePermittedPlatesGroups();
  const [selectedEventTypes, setSelectedEventTypes] = useState<EventType[]>([]);

  const { bind: plate } = useInput<string>(props.plate?.license_plate || '');
  const { bind: state } = useInput<string>(props.plate?.state_code || getDefaultState().abbr);
  const { bind: group } = useInput<string>(props.plate?.categorization_code || '');

  const save = (evt: any): boolean => {
    evt.preventDefault();
    evt.stopPropagation();

    const newPlate: PostPermittedPlate = {
      permitted_plate_id: props.plate?.permitted_plate_id,
      license_plate: plate.value.toUpperCase(),
      state_code: state.value.toUpperCase(),
      categorization_code: group.value as PriviledgedGroup,
      event_type_identifiers: selectedEventTypes.map((et) => et.identifier),
    };

    if (!newPlate.categorization_code) {
      new Alert('Please select a privileged group.').alert();
      return false;
    }

    if (
      exceptions.some(
        (e) =>
          e.license_plate.toUpperCase() === newPlate.license_plate.toUpperCase() &&
          e.state_code.toUpperCase() === newPlate.state_code.toUpperCase(),
      )
    ) {
      new Alert(`License ${newPlate.license_plate} (${newPlate.state_code}) plate is already on the permitted list!`).alert();
      return false;
    }

    setSaving(true);

    // edit existing one
    PermittedPlatesService.createOrUpdate(newPlate, fleet.uuid)
      .then(() => {
        invalidatePermittedPlates(fleet.uuid);
        setSaving(false);
        props.toggle();
      })
      .finally(() => {
        setSaving(false);
      });

    return false;
  };

  const removeEventType = (eventType: EventType) => () => {
    if (!eventType.id) {
      return;
    }
    setSelectedEventTypes((types) => types.filter((t) => t.id !== eventType.id));
  };

  return (
    <Modal isOpen onToggle={toggle} data-test="addPlateModal" contentLabel="License plate exception">
      <Padded>
        <H3>{props.plate ? 'Edit ' : 'Add new '}license plate exception</H3>
      </Padded>

      <Hr />

      <Form onSubmit={save}>
        <Padded>
          <FormGroup>
            <FormControl
              {...plate}
              placeholder="License plate number"
              autoFocus
              required
              minLength={3}
              style={{ textTransform: plate.value.length > 0 ? 'uppercase' : 'none' }}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>State</FormLabel>
            <FormControl as="select" custom {...state}>
              <option value="">Please select</option>
              {Regions.map((r) => (
                <optgroup label={r.label} key={r.label}>
                  {r.states.map((s) => (
                    <option value={s.region} key={s.region}>
                      {s.state}
                    </option>
                  ))}
                </optgroup>
              ))}
            </FormControl>
          </FormGroup>

          <FormGroup>
            <FormLabel>Event types</FormLabel>
            <EventTypesFilterBrowser
              selectedEventTypes={selectedEventTypes}
              setSelectedEventTypes={setSelectedEventTypes}
              required={selectedEventTypes.length === 0}
              allowAll
            />

            {selectedEventTypes?.map((et: EventType) => (
              <ButtonTag key={et.id} onClick={removeEventType(et)}>
                {humanizeEnum(et.identifier)} ×
              </ButtonTag>
            ))}
          </FormGroup>

          <FormGroup>
            <FormLabel>Privileged group</FormLabel>
            <FormControl {...group} placeholder="Privileged group" required minLength={3} list="groups" />

            <datalist id="groups">
              {groups.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </datalist>
          </FormGroup>

          <ButtonsContainer>
            <ProgressButton variant="primary" block type="submit" data-accesskey="Enter" data-disableKey isLoading={isSaving}>
              Save
            </ProgressButton>

            <Button variant="secondary" onClick={toggle} block>
              Cancel
            </Button>
          </ButtonsContainer>
        </Padded>
      </Form>
    </Modal>
  );
}
